import React, { useState, useEffect, useRef } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import { FaCircleHalfStroke } from "react-icons/fa6";
import { useParams, useNavigate } from "react-router-dom";

const themes = {
  "blue-yellow": {
    background: "#08306b",
    foreground: "#fffd33",
  },
  "black-white": {
    background: "#000000",
    foreground: "#ffffff",
  },
  "white-black": {
    background: "#ffffff",
    foreground: "#000000",
  },
};

function MenuPage() {
  const [theme, setTheme] = useState("blue-yellow");
  const [language, setLanguage] = useState("gr");
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cocktails, setCocktails] = useState([]);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [ageVerified, setAgeVerified] = useState(true); // disabled 18+ check for now

  const audioRef = useRef(null);
  const { barName } = useParams();
  const navigate = useNavigate();

  const colors = themes[theme];

  const emptyCocktail = {
    name: { en: "", gr: "" },
    description: { en: "", gr: "" },
    price: "",
    ingredients: { en: [], gr: [] },
    audio: { en: null, gr: null },
  };

  const current = cocktails[index] || emptyCocktail;

  // Disable scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = ageVerified === null ? "hidden" : "auto";
  }, [ageVerified]);

  // Fetch cocktails
  useEffect(() => {
    if (!barName) return;
    fetch(`${process.env.REACT_APP_API_URL}/cocktails/for-menu/${barName}`)
      .then((res) => res.json())
      .then((data) => {
        const sorted = [...data].sort((a, b) => {
          const nameA = a.name?.[language] || "";
          const nameB = b.name?.[language] || "";

          const isDigitA = /^\d/.test(nameA);
          const isDigitB = /^\d/.test(nameB);

          if (isDigitA && !isDigitB) return 1; // letters first
          if (!isDigitA && isDigitB) return -1; // digits after letters

          if (isDigitA && isDigitB) {
            return parseFloat(nameA) - parseFloat(nameB); // numeric sort
          }

          return nameA.localeCompare(nameB); // alphabetic sort
        });

        setCocktails(sorted);
      })
      .catch((err) => console.error("Failed to load cocktails:", err));
  }, [barName]);

  // Unlock audio
  useEffect(() => {
    const unlockAudio = () => {
      setAudioUnlocked(true);
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };
    window.addEventListener("click", unlockAudio);
    window.addEventListener("touchstart", unlockAudio);
    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };
  }, []);

  // Play audio
  useEffect(() => {
    if (cocktails.length > 0 && audioUnlocked) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.load();
        audio
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    }
  }, [cocktails, audioUnlocked]);

  // Replay on index/language change
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUnlocked) return;
    audio.pause();
    audio.currentTime = 0;
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => setIsPlaying(false));
    audio.onended = () => setIsPlaying(false);
  }, [index, language, audioUnlocked]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        setAudioUnlocked(true);
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.error("Play failed:", err);
        setIsPlaying(false);
      }
    }
  };

  // Swipe handling
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEndX(0);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    if (distance > minSwipeDistance && index < cocktails.length - 1) {
      setIndex((i) => i + 1);
    } else if (distance < -minSwipeDistance && index > 0) {
      setIndex((i) => i - 1);
    }
  };

  const name = current.name?.[language] || "Unnamed";
  const price = current.price || "";
  const ingredients = current.ingredients?.[language] || [];
  const description = current.description?.[language] || "";
  const flavor = current.flavor?.[language] || "";

  return (
    <div
      style={{
        ...styles.page,
        backgroundColor: colors.background,
        color: colors.foreground,
      }}
    >
      {/* Age Verification */}
      {ageVerified === null && (
        <div style={styles.modalOverlay}>
          <div
            style={{
              ...styles.modal,
              backgroundColor: colors.background,
              color: colors.foreground,
              border: `2px solid ${colors.foreground}`,
            }}
          >
            <h2 style={styles.modalTitle}>Age Verification Required</h2>
            <p style={styles.modalText}>
              This menu includes alcoholic beverages.
              <br />
              Please confirm your age to continue.
            </p>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setAgeVerified(true)}
                style={{
                  ...styles.modalButton,
                  backgroundColor: colors.foreground,
                  color: colors.background,
                }}
              >
                I am 18 or older
              </button>
              <button
                onClick={() => {
                  alert("Access denied. Redirecting to landing page.");
                  navigate("/");
                }}
                style={{
                  ...styles.modalButton,
                  backgroundColor: "#990000",
                  color: "#fff",
                }}
              >
                I am under 18
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header style={styles.header}>
        <div
          style={{
            ...styles.logoContainer,
            border: `1.5px solid ${colors.foreground}`,
            boxShadow:
              theme === "blue-yellow"
                ? `0 0 6px ${colors.foreground}80`
                : theme === "black-white"
                ? `0 0 5px rgba(255,255,255,0.3)`
                : `0 0 4px rgba(0,0,0,0.25)`,
            background:
              theme === "black-white"
                ? "#111" // subtle lift from pure black
                : "transparent",
          }}
          onClick={() => navigate("/")}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <img
            src={
              theme === "black-white"
                ? "/VISION LOGO trnsw.png" // white logo for dark background
                : "/VISION LOGO trnsB.png" // black logo for light or color background
            }
            alt="VISION by Metron Ariston"
            style={{
              ...styles.logo,
              filter:
                theme === "blue-yellow"
                  ? "invert(90%) brightness(110%)"
                  : "none",
            }}
          />
        </div>

        <div style={styles.langThemeGroup}>
          <button
            style={{
              ...styles.language,
              background: colors.foreground,
              color: colors.background,
            }}
            onClick={() => setLanguage((lang) => (lang === "en" ? "gr" : "en"))}
          >
            {language.toUpperCase()}
          </button>
          <button
            style={{
              ...styles.themeButton,
              borderColor: colors.foreground,
              color: colors.foreground,
            }}
            onClick={() =>
              setTheme((prev) =>
                prev === "blue-yellow"
                  ? "black-white"
                  : prev === "black-white"
                  ? "white-black"
                  : "blue-yellow"
              )
            }
            title="Toggle Theme"
          >
            <FaCircleHalfStroke />
          </button>
        </div>
      </header>


      {/* Main content */}
      <main
        style={{ ...styles.card, backgroundColor: colors.background }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <h1 style={styles.name}>{name}</h1>
        {parseFloat(price.replace(/[^\d.]/g, "")) > 0 && (
          <h2 style={styles.price}>{price}</h2>
        )}
        <ul style={styles.ingredients}>
          {ingredients.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
        {description && <p style={styles.description}>{description}</p>}
        {flavor && <p style={styles.flavor}>{flavor}</p>}
        <div style={styles.audioControl}>
          <button
            onClick={togglePlayback}
            style={{ ...styles.audioButton, color: colors.foreground }}
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <audio
            ref={audioRef}
            preload="auto"
            playsInline
            src={current.audio?.[language] || ""}
          />
        </div>
      </main>

      {/* Pagination */}
      <div style={styles.pagination}>
        <button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          style={{
            ...styles.navButton,
            backgroundColor: colors.foreground,
            color: colors.background,
          }}
        >
          ◀
        </button>
        <span>
          {index + 1} / {cocktails.length}
        </span>
        <button
          onClick={() => setIndex((i) => Math.min(cocktails.length - 1, i + 1))}
          style={{
            ...styles.navButton,
            backgroundColor: colors.foreground,
            color: colors.background,
          }}
        >
          ▶
        </button>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <a
          href="/contact"
          style={{
            ...styles.contactButton,
            backgroundColor: colors.foreground,
            color: colors.background,
          }}
        >
          Contact
        </a>
        <p>© Metron Ariston [2024]</p>
        <p>VISION by METRON ARISTON – Making Responsibility a Trend™</p>
        <p>All rights reserved.</p>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    fontFamily: "Futura, sans-serif",
    padding: "20px 10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  header: {
    width: "100%",
    maxWidth: "500px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  logoContainer: {
    borderRadius: "12px",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.4s ease",
  },
  logo: {
    height: "60px",
    objectFit: "contain",
    transition: "transform 0.2s ease-in-out, filter 0.5s ease",
  },
  langThemeGroup: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  language: {
    border: "none",
    borderRadius: "5px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  themeButton: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "2px solid",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    lineHeight: "1",
  },
  card: {
    padding: "30px 25px",
    borderRadius: "12px",
    width: "85%",
    maxWidth: "500px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    marginBottom: "40px",
  },
  name: {
    fontSize: "36px",
    marginBottom: "10px",
    fontWeight: "bold",
  },
  price: {
    fontSize: "26px",
    marginBottom: "20px",
  },
  ingredients: {
    listStyleType: "disc",
    paddingLeft: "25px",
    textAlign: "left",
    fontSize: "20px",
    lineHeight: "1.6",
    marginBottom: "20px",
  },
  description: {
    fontSize: "17px",
    marginTop: "10px",
    marginBottom: "10px",
    fontStyle: "italic",
  },
  flavor: {
    fontSize: "18px",
    fontStyle: "italic",
    marginTop: "10px",
  },
  audioControl: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
  },
  audioButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "28px",
    cursor: "pointer",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    fontSize: "16px",
    marginBottom: "30px",
  },
  navButton: {
    fontSize: "18px",
    border: "none",
    borderRadius: "6px",
    padding: "8px 16px",
    cursor: "pointer",
    fontWeight: "bold",
  },
  footer: {
    marginTop: "auto",
    fontSize: "13px",
    color: "#ccc",
    textAlign: "center",
    paddingTop: "30px",
  },
  contactButton: {
    margin: "10px 0",
    textDecoration: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    fontWeight: "bold",
    fontSize: "14px",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(4px)",
    zIndex: 9999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
  modal: {
    width: "90%",
    maxWidth: "360px",
    borderRadius: "10px",
    padding: "20px",
    textAlign: "center",
    boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
  },
  modalTitle: {
    fontSize: "18px",
    marginBottom: "10px",
  },
  modalText: {
    fontSize: "14px",
    marginBottom: "20px",
  },
  modalButtons: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  modalButton: {
    padding: "10px",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "bold",
    cursor: "pointer",
  },
};

export default MenuPage;